<template>
  <div v-if="show" class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm">
    <div class="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      <!-- Modal Header -->
      <div class="flex justify-between items-center p-4 border-b flex-shrink-0">
        <h2 class="text-xl font-semibold">New Institution</h2>
        <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Stepper Navigation -->
      <div class="flex border-b overflow-x-auto flex-shrink-0">
        <button @click="currentStep = 'institution-type'"
          class="px-8 py-4 font-medium text-gray-600 border-b-2 transition-all"
          :class="{ 'border-orange-400 text-blue-900': currentStep === 'institution-type', 'border-transparent': currentStep !== 'institution-type' }">
          Institution Type
        </button>
        <button @click="currentStep = 'institution'"
          v-if="formData.institution_type !== 'individual'"
          class="px-8 py-4 font-medium text-gray-600 border-b-2 transition-all"
          :class="{ 'border-orange-400 text-blue-900': currentStep === 'institution', 'border-transparent': currentStep !== 'institution' }">
          Institution Information
        </button>
        <button @click="currentStep = 'admin'" class="px-8 py-4 font-medium text-gray-600 border-b-2 transition-all"
          :class="{ 'border-orange-400 text-blue-900': currentStep === 'admin', 'border-transparent': currentStep !== 'admin' }">
          Admin Information
        </button>
        <button @click="currentStep = 'fee'" class="px-8 py-4 font-medium text-gray-600 border-b-2 transition-all"
          :class="{ 'border-orange-400 text-blue-900': currentStep === 'fee', 'border-transparent': currentStep !== 'fee' }">
          Onboarding Fee
        </button>
      </div>

      <!-- Modal Content Area -->
      <div class="flex-1 overflow-y-auto">
        <!-- Institution Type Information Step -->
        <div v-if="currentStep === 'institution-type'" class="p-6">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              <span class="text-red-500">*</span>Institution Type
            </label>
            <select
              v-model="formData.institution_type"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            >
              <option value="" disabled>Select an institution type...</option>
              <option value="institution">Institution</option>
              <option value="individual">Individual</option>
            </select>
          </div>
        </div>

      <!-- Institution Information Step -->
      <div v-if="currentStep === 'institution'" class="p-6">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Institution Name
          </label>
          <input type="text" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.name">
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Email Address
          </label>
          <input type="email" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.email">
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Website URL (Optional)
          </label>
          <input type="url" placeholder="Type/Paste URL..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.website_url">
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Regulatory Body
          </label>
          <select
            v-model="formData.nita_number"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
          >
            <option value="" disabled>Select a regulatory body...</option>
            <option v-for="body in professionalBodies" :key="body.id" :value="body.name">
              {{ body.name }}
            </option>
          </select>
          <div v-if="loadingProfessionalBodies" class="mt-1 text-sm text-gray-500">Loading regulatory bodies...</div>
          <div v-if="!loadingProfessionalBodies && professionalBodies.length === 0" class="mt-1 text-sm text-gray-500">No regulatory bodies available</div>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Institution Registration Number
          </label>
          <input type="text" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.incorporation_number">
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Certificate of Incorporation
          </label>
          <div class="border border-dashed border-gray-300 rounded-md p-4 text-center hover:bg-gray-50 cursor-pointer"
            @click="$refs.documentInput.click()">
            <input ref="documentInput" type="file" class="hidden" @change="handleDocumentUpload"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
            <div v-if="!formData.institution_document" class="flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500 mr-2" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload Document or click to browse</span>
            </div>
            <div v-else class="flex justify-between items-center">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500 mr-2" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm truncate max-w-xs">{{ formData.institution_document.name }}</span>
              </div>
              <button type="button" @click.stop="removeDocument" class="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <p v-if="documentError" class="mt-1 text-sm text-red-600">{{ documentError }}</p>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Institution Logo
          </label>
          <div class="border border-dashed border-gray-300 rounded-md p-4 text-center hover:bg-gray-50 cursor-pointer"
            @click="$refs.logoInput.click()">
            <input ref="logoInput" type="file" class="hidden" @change="handleLogoUpload"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml">
            <div v-if="!formData.logo_url" class="flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500 mr-2" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload Logo</span>
            </div>
            <div v-else class="flex flex-col items-center">
              <img :src="logoPreview" class="h-20 w-auto object-contain mb-2" alt="Institution Logo Preview" />
              <div class="flex justify-between items-center w-full">
                <span class="text-sm truncate max-w-xs">{{ formData.logo_url.name }}</span>
                <button type="button" @click.stop="removeLogo" class="text-red-500 hover:text-red-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <p v-if="logoError" class="mt-1 text-sm text-red-600">{{ logoError }}</p>
        </div>
      </div>

      <!-- Admin Information Step -->
      <div v-if="currentStep === 'admin'" class="p-6">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Admin Name
          </label>
          <input type="text" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.admin_name">
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Admin Email Address
          </label>
          <input type="email" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.admin_email">
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Admin Phone Number
          </label>
          <input type="tel" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.admin_phone">
        </div>
        <div v-if="formData.institution_type === 'individual'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>ID number
          </label>
          <input type="text" placeholder="Type..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.id_number">
        </div>
        <div v-if="formData.institution_type === 'individual'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Regulatory Body
          </label>
          <select
            v-model="formData.nita_number"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
          >
            <option value="" disabled>Select a regulatory body...</option>
            <option v-for="body in professionalBodies" :key="body.id" :value="body.name">
              {{ body.name }}
            </option>
          </select>
          <div v-if="loadingProfessionalBodies" class="mt-1 text-sm text-gray-500">Loading regulatory bodies...</div>
          <div v-if="!loadingProfessionalBodies && professionalBodies.length === 0" class="mt-1 text-sm text-gray-500">No regulatory bodies available</div>
        </div>
        <div v-if="formData.institution_type === 'individual'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Upload ID Picture
          </label>
          <div class="border border-dashed border-gray-300 rounded-md p-4 text-center hover:bg-gray-50 cursor-pointer"
            @click="$refs.idInput.click()">
            <input ref="idInput" type="file" class="hidden" @change="handleLogoUpload"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
            <div v-if="!formData.logo_url" class="flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500 mr-2" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload ID Picture</span>
            </div>
            <div v-else class="flex justify-between items-center">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500 mr-2" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm truncate max-w-xs">{{ formData.logo_url.name }}</span>
              </div>
              <button type="button" @click.stop="removeLogo" class="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <p v-if="logoError" class="mt-1 text-sm text-red-600">{{ logoError }}</p>
        </div>
        <div v-if="formData.institution_type === 'individual'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Upload Higher Education Certificate
          </label>
          <div class="border border-dashed border-gray-300 rounded-md p-4 text-center hover:bg-gray-50 cursor-pointer"
            @click="$refs.certificateInput.click()">
            <input ref="certificateInput" type="file" class="hidden" @change="handleDocumentUpload"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
            <div v-if="!formData.institution_document" class="flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500 mr-2" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload Higher Education Certificate</span>
            </div>
            <div v-else class="flex justify-between items-center">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500 mr-2" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm truncate max-w-xs">{{ formData.institution_document.name }}</span>
              </div>
              <button type="button" @click.stop="removeDocument" class="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <p v-if="documentError" class="mt-1 text-sm text-red-600">{{ documentError }}</p>
        </div>
        <div v-if="formData.institution_type === 'individual'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <span class="text-red-500">*</span>Certificate of Incorporation
          </label>
          <div class="border border-dashed border-gray-300 rounded-md p-4 text-center hover:bg-gray-50 cursor-pointer"
            @click="$refs.certificateInput.click()">
            <input ref="certificateInput" type="file" class="hidden" @change="handleDocumentUpload"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
            <div v-if="!formData.institution_document" class="flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500 mr-2" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload Certificate of Incorporation</span>
            </div>
            <div v-else class="flex justify-between items-center">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-5 text-green-500 mr-2" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm truncate max-w-xs">{{ formData.institution_document.name }}</span>
              </div>
              <button type="button" @click.stop="removeDocument" class="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <p v-if="documentError" class="mt-1 text-sm text-red-600">{{ documentError }}</p>
        </div>
        <div v-if="formData.institution_type === 'individual'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Organisation Website URL (Optional)
          </label>
          <input type="url" placeholder="Type/Paste URL..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200"
            v-model="formData.website_url">
        </div>
      </div>

      <!-- Onboarding Fee Step -->
      <div v-if="currentStep === 'fee'" class="p-6">
        <div class="flex items-start bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-orange-400" viewBox="0 0 20 20"
            fill="currentColor">
            <path fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
              clip-rule="evenodd" />
          </svg>
          <div class="ml-3">
            <p class="text-sm text-orange-400">
              Pending Verification!<br>
              Payment has to be done to run verification
            </p>
          </div>
        </div>
        <h3 class="text-gray-700 font-medium mb-4">Payment Request</h3>
        <div v-if="showSuccessMessage" class="border border-gray-200 rounded-md p-6 mb-6 text-center">
          <div class="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p class="text-gray-700">
            You've successfully sent a payment request for KES 20,000 to {{ formData.admin_phone }} via M-Pesa and
            registered the institution.
          </p>
        </div>
        <div v-else class="border border-gray-200 rounded-md p-6 mb-6">
          <div class="flex justify-center mb-4">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png"
              alt="M-Pesa" class="h-10" />
          </div>
          <p class="text-center mb-4">In the field below, enter the M-Pesa number to receive a payment request for KES
            20,000.</p>
          <div class="mb-4">
            <div class="relative">
              <div class="flex">
                <span
                  class="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-100 text-gray-700">
                  +254
                </span>
                <input type="tel" placeholder="e.g 70024xxxx"
                  class="w-full px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-orange-200"
                  v-model="phoneNumberDigits" @input="updatePhoneNumber" maxlength="9" pattern="[0-9]*">
              </div>
            </div>
          </div>
          <div class="flex justify-center">
            <button @click="sendPaymentPrompt" :disabled="isLoading || !formData.admin_phone"
              class="bg-orange-400 hover:bg-orange-300 text-white font-medium py-2 px-6 rounded transition-colors flex items-center"
              :class="{ 'opacity-50 cursor-not-allowed': isLoading || !formData.admin_phone }">
              <span v-if="isLoading" class="flex items-center">
                <svg class="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                  viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                  </path>
                </svg>
                Processing...
              </span>
              <span v-else>Send Prompt</span>
            </button>
          </div>
        </div>
      </div>
      </div>

      <!-- Modal Footer -->
      <div class="bg-gray-50 px-6 py-4 flex justify-between border-t flex-shrink-0">
        <button v-if="currentStep !== 'institution-type'" @click="previousStep"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors">
          Previous
        </button>
        <div v-else></div>
        <div class="flex space-x-3">
          <button v-if="currentStep !== 'fee'" @click="nextStep"
            class="px-6 py-2 bg-orange-500 hover:bg-orange-300 text-white rounded-md transition-colors">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { InstitutionsAPI } from '@/services';
import AWS from 'aws-sdk';
import { useToast } from 'vue-toastification';

export default {
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      currentStep: "institution-type",
      formData: {
        name: "",
        email: "",
        id_number: "",
        website_url: "",
        nita_number: "",
        incorporation_number: "",
        institution_document: null,
        logo_url: null,
        reg_type: "BD",
        admin_name: "",
        admin_email: "",
        admin_phone: "",
        institution_document_url: "",
        logo_url_uploaded: "",
        institution_type: "institution",
      },
      phoneNumberDigits: "",
      logoPreview: null,
      documentError: null,
      logoError: null,
      isLoading: false,
      showSuccessMessage: false,
      professionalBodies: [],
      loadingProfessionalBodies: true,
    };
  },
  mounted() {
    this.fetchProfessionalBodies();
  },
  methods: {
    resetForm() {
      this.formData = {
        name: "",
        email: "",
        id_number: "",
        website_url: "",
        nita_number: "",
        incorporation_number: "",
        institution_document: null,
        logo_url: null,
        reg_type: "BD",
        admin_name: "",
        admin_email: "",
        admin_phone: "",
        institution_document_url: "",
        logo_url_uploaded: "",
        institution_type: "institution",
      };
      this.phoneNumberDigits = "";
      this.logoPreview = null;
      this.documentError = null;
      this.logoError = null;
      this.isLoading = false;
      this.showSuccessMessage = false;
      this.currentStep = "institution-type";
    },
    nextStep() {
      if (this.currentStep === "institution-type") {
        if (this.validateInstitutionTypeStep()) {
          if (this.formData.institution_type === 'institution') {
            this.currentStep = "institution";
          } else {
            this.currentStep = "admin";
          }
        }
      } else if (this.currentStep === "institution") {
        if (this.validateInstitutionStep()) {
          this.currentStep = "admin";
        }
      } else if (this.currentStep === "admin") {
        if (this.validateAdminStep()) {
          this.currentStep = "fee";
        }
      }
    },
    previousStep() {
      if (this.currentStep === "institution") {
        this.currentStep = "institution-type";
      } else if (this.currentStep === "admin") {
        if (this.formData.institution_type === 'institution') {
          this.currentStep = "institution";
        } else {
          this.currentStep = "institution-type";
        }
      } else if (this.currentStep === "fee") {
        this.currentStep = "admin";
      }
    },
    validateInstitutionTypeStep() {
      return !!this.formData.institution_type;
    },
    validateInstitutionStep() {
      let isValid = true;
      if (!this.formData.name || !this.formData.email || !this.formData.nita_number || !this.formData.incorporation_number) {
        isValid = false;
      }
      if (!this.formData.institution_document) {
        this.documentError = "Registration document is required";
        isValid = false;
      } else {
        this.documentError = null;
      }
      if (!this.formData.logo_url) {
        this.logoError = "Institution logo is required";
        isValid = false;
      } else {
        this.logoError = null;
      }
      return isValid;
    },
    validateAdminStep() {
      return this.formData.admin_name && this.formData.admin_email && this.formData.admin_phone;
    },
    async fetchProfessionalBodies() {
      try {
        console.log('Fetching professional bodies...');
        const response = await InstitutionsAPI.getProfessionalBodies();
        console.log('Professional bodies response:', response);
        this.professionalBodies = response.data.professional_bodies || [];
        this.loadingProfessionalBodies = false;
        console.log('Professional bodies set:', this.professionalBodies);
      } catch (error) {
        console.error("Error fetching professional bodies:", error);
        this.toast.error('Error fetching professional bodies');
        this.loadingProfessionalBodies = false;
      }
    },
    handleDocumentUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.documentError = "Please upload a PDF, DOC, DOCX, or image file";
        return;
      }

      const maxSize = 15 * 1024 * 1024; // 15MB
      if (file.size > maxSize) {
        this.documentError = "File size should not exceed 15MB";
        return;
      }

      this.formData.institution_document = file;
      this.documentError = null;
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        this.toast.warning("Please upload a JPG, PNG, or SVG image");
        return;
      }

      const maxSize = 15 * 1024 * 1024; // 15MB
      if (file.size > maxSize) {
        this.toast.warning("Image size should not exceed 15MB");
        return;
      }

      this.formData.logo_url = file;
      this.logoError = null;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeDocument() {
      this.formData.institution_document = null;
      this.formData.institution_document_url = "";
    },
    removeLogo() {
      this.formData.logo_url = null;
      this.formData.logo_url_uploaded = "";
      this.logoPreview = null;
    },
    updatePhoneNumber() {
      this.phoneNumberDigits = this.phoneNumberDigits.replace(/[^0-9]/g, '');
      this.formData.admin_phone = "+254" + this.phoneNumberDigits;
    },
    async uploadFile(file) {
      if (!file) {
        console.warn("No file selected.");
        return "";
      }

      const s3 = new AWS.S3({
        endpoint: 'lon1.digitaloceanspaces.com',
        signatureVersion: 'v4',
        accessKeyId: 'DO00PK2KLQ4HUBKTB6R3',
        secretAccessKey: 'L3yOKmH3Dqfpc3tVZv4Mz5O+jd1bXTobKS//zmY72Rk',
        region: 'lon1',
      });

      const params = {
        Bucket: 'jiji-bucket',
        Key: `${Date.now()}_${file.name}`,
        Body: file,
        ACL: 'public-read',
        ContentType: file.type,
      };

      try {
        const result = await s3.upload(params).promise();
        return result.Location;
      } catch (error) {
        console.error('Detailed upload error:', {
          message: error.message,
          code: error.code,
          statusCode: error.statusCode,
          requestId: error.requestId,
          details: error,
        });
        throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
      }
    },
    async sendPaymentPrompt() {
      if (!this.formData.admin_phone || !this.formData.admin_phone.startsWith('+254')) {
        this.toast.warning("Please enter a valid M-Pesa number starting with +254");
        return;
      }

      if (this.formData.institution_type === 'institution') {
        if (!this.validateInstitutionStep() || !this.validateAdminStep()) {
          this.toast.warning("Please complete all required fields in previous steps.");
          return;
        }
      } else {
        if (!this.validateAdminStep() || !this.formData.logo_url || !this.formData.institution_document) {
          this.toast.warning("Please upload ID picture and higher education certificate.");
          return;
        }
      }

      this.isLoading = true;

      try {
        if (this.formData.institution_document) {
          this.formData.institution_document_url = await this.uploadFile(this.formData.institution_document);
        }
        if (this.formData.logo_url) {
          this.formData.logo_url_uploaded = await this.uploadFile(this.formData.logo_url);
        }

        setTimeout(async () => {
          this.isLoading = false;
          this.showSuccessMessage = true;

          const payload = new FormData();
          if (this.formData.institution_type === 'institution') {
            payload.append('name', this.formData.name);
            payload.append('email', this.formData.email);
          } else {
            payload.append('name', this.formData.admin_name);
            payload.append('email', this.formData.admin_email);
            payload.append('id_number', this.formData.id_number || '');
          }
          payload.append('logo_url', this.formData.logo_url_uploaded);
          payload.append('nita_number', this.formData.nita_number);
          payload.append('incorporation_number', this.formData.incorporation_number);
          payload.append('institution_document', this.formData.institution_document_url);
          payload.append('reg_type', this.formData.reg_type);
          payload.append('website_url', this.formData.website_url || '');
          payload.append('admin_name', this.formData.admin_name);
          payload.append('admin_email', this.formData.admin_email);
          payload.append('admin_phone', this.formData.admin_phone);

          try {
            await InstitutionsAPI.create(payload);
            this.$emit('institution-created');
            this.$emit('close');
            this.resetForm();
            this.toast.success("Institution created successfully!");
          } catch (error) {
            this.toast.error(`Failed to create institution: ${error.message}`);
            this.showSuccessMessage = false;
          }
        }, 3000);
      } catch (error) {
        this.isLoading = false;
        this.toast.error(`Failed to upload files: ${error.message}`);
      }
    },
  },
};
</script>

<style>
/* Custom styles unchanged */

/* Ensure dropdown options are visible */
select option {
  background-color: white;
  color: #374151;
  padding: 8px;
  z-index: 9999;
}

/* Fix modal overflow for dropdowns */
.modal-content {
  overflow: visible !important;
}

select:focus {
  z-index: 9999;
}
</style>